import { useState } from 'react';
import { Helmet } from 'react-helmet';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { z } from 'zod';

const contactSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Please enter a valid email address").max(255, "Email must be less than 255 characters"),
  social: z.string().trim().max(100, "Social handle must be less than 100 characters").optional(),
  message: z.string().trim().min(1, "Message is required").max(1000, "Message must be less than 1000 characters")
});

type ContactFormData = z.infer<typeof contactSchema>;

const Contact = () => {
  const [formData, setFormData] = useState<ContactFormData>({
    name: '',
    email: '',
    social: '',
    message: ''
  });
  const [errors, setErrors] = useState<Partial<Record<keyof ContactFormData, string>>>({});
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showAlert = (message: string, type: 'success' | 'error') => {
    setAlert({ message, type });
    setTimeout(() => setAlert(null), 5000);
  };

  const validateForm = () => {
    try {
      contactSchema.parse(formData);
      setErrors({});
      return true;
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: Partial<Record<keyof ContactFormData, string>> = {};
        error.errors.forEach((err) => {
          if (err.path[0]) {
            newErrors[err.path[0] as keyof ContactFormData] = err.message;
          }
        });
        setErrors(newErrors);
      }
      return false;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      showAlert('Please fill in all required fields correctly.', 'error');
      return;
    }

    setLoading(true);

    try {
      const emailBody = `
Name: ${formData.name}
Email: ${formData.email}
Social Media: ${formData.social || 'N/A'}
Message:

${formData.message}
      `.trim();

      const subject = encodeURIComponent(`Contact From ${formData.name}`);
      const body = encodeURIComponent(emailBody);
      window.location.href = `mailto:invizher@gmail.com?subject=${subject}&body=${body}`;

      showAlert('Sending message...', 'success');
      setFormData({ name: '', email: '', social: '', message: '' });
    } catch (error) {
      showAlert('An error occurred. Please try again later.', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Contact Us | InvizHer - Get in Touch</title>
        <meta name="description" content="Get in touch with InvizHer. Share your thoughts, inquiries, or collaboration ideas. We're eager to hear from you and respond to your messages." />
        <meta name="keywords" content="contact InvizHer, get in touch, contact form, reach out, feedback, inquiries" />
        <link rel="canonical" href="https://invizher.com/contact" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <main className="pt-24 md:pt-32 pb-16" role="main">
          {/* Contact Hero Section */}
          <section className="container mx-auto px-4 sm:px-6 py-8 md:py-16">
            <div className="text-center max-w-3xl mx-auto animate-fade-in">
              <div className="inline-block mb-4">
                <div className="w-16 h-16 md:w-20 md:h-20 mx-auto rounded-2xl bg-gradient-to-br from-blue-500/10 to-purple-500/10 flex items-center justify-center">
                  <i className="fas fa-envelope text-3xl md:text-4xl gradient-text"></i>
                </div>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4 md:mb-6">
                Get in <span className="gradient-text">Touch</span>
              </h1>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed px-4">
                Have questions or want to collaborate? We'd love to hear from you. Fill out the form below and we'll get back to you soon.
              </p>
            </div>
          </section>

          {/* Contact Form Section */}
          <section className="container mx-auto px-4 sm:px-6 pb-8 md:pb-16">
            <div className="max-w-4xl mx-auto">
              <div className="glass-morphism rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-12 shadow-xl">
              <form onSubmit={handleSubmit} className="space-y-8">
                {/* Alert Container */}
                {alert && (
                  <div className={`rounded-lg p-4 text-sm ${
                    alert.type === 'success' 
                      ? 'bg-green-50 border border-green-200 text-green-800' 
                      : 'bg-red-50 border border-red-200 text-red-800'
                  }`}>
                    {alert.message}
                  </div>
                )}

                {/* Name Input Group */}
                <div className="relative">
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className={`peer w-full px-6 py-4 bg-white rounded-xl focus:outline-none transition-all duration-300 border-2 ${
                      errors.name ? 'border-red-500 bg-red-50' : 'border-transparent focus:border-blue-500 focus:shadow-[0_0_0_4px_rgba(59,130,246,0.1)]'
                    }`}
                    placeholder=" "
                  />
                  <label
                    htmlFor="name"
                    className="absolute left-6 top-4 text-gray-500 pointer-events-none transition-all duration-300 peer-focus:top-[-0.625rem] peer-focus:text-sm peer-focus:text-blue-500 peer-[:not(:placeholder-shown)]:top-[-0.625rem] peer-[:not(:placeholder-shown)]:text-sm"
                  >
                    Your Name
                  </label>
                  {errors.name && (
                    <span className="text-red-500 text-sm mt-1 block">{errors.name}</span>
                  )}
                </div>

                {/* Email Input Group */}
                <div className="relative">
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={`peer w-full px-6 py-4 bg-white rounded-xl focus:outline-none transition-all duration-300 border-2 ${
                      errors.email ? 'border-red-500 bg-red-50' : 'border-transparent focus:border-blue-500 focus:shadow-[0_0_0_4px_rgba(59,130,246,0.1)]'
                    }`}
                    placeholder=" "
                  />
                  <label
                    htmlFor="email"
                    className="absolute left-6 top-4 text-gray-500 pointer-events-none transition-all duration-300 peer-focus:top-[-0.625rem] peer-focus:text-sm peer-focus:text-blue-500 peer-[:not(:placeholder-shown)]:top-[-0.625rem] peer-[:not(:placeholder-shown)]:text-sm"
                  >
                    Your Email
                  </label>
                  {errors.email && (
                    <span className="text-red-500 text-sm mt-1 block">{errors.email}</span>
                  )}
                </div>

                {/* Social Media Handle Group */}
                <div className="relative">
                  <input
                    type="text"
                    id="social"
                    name="social"
                    value={formData.social}
                    onChange={(e) => setFormData({ ...formData, social: e.target.value })}
                    className="peer w-full px-6 py-4 bg-white rounded-xl focus:outline-none transition-all duration-300 border-2 border-transparent focus:border-blue-500 focus:shadow-[0_0_0_4px_rgba(59,130,246,0.1)]"
                    placeholder=" "
                  />
                  <label
                    htmlFor="social"
                    className="absolute left-6 top-4 text-gray-500 pointer-events-none transition-all duration-300 peer-focus:top-[-0.625rem] peer-focus:text-sm peer-focus:text-blue-500 peer-[:not(:placeholder-shown)]:top-[-0.625rem] peer-[:not(:placeholder-shown)]:text-sm"
                  >
                    Social Media Handle (Optional)
                  </label>
                </div>

                {/* Message Group */}
                <div className="relative">
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={6}
                    className={`peer w-full px-6 py-4 bg-white rounded-xl focus:outline-none resize-none transition-all duration-300 border-2 ${
                      errors.message ? 'border-red-500 bg-red-50' : 'border-transparent focus:border-blue-500 focus:shadow-[0_0_0_4px_rgba(59,130,246,0.1)]'
                    }`}
                    placeholder=" "
                  />
                  <label
                    htmlFor="message"
                    className="absolute left-6 top-4 text-gray-500 pointer-events-none transition-all duration-300 peer-focus:top-[-0.625rem] peer-focus:text-sm peer-focus:text-blue-500 peer-[:not(:placeholder-shown)]:top-[-0.625rem] peer-[:not(:placeholder-shown)]:text-sm"
                  >
                    Your Message
                  </label>
                  {errors.message && (
                    <span className="text-red-500 text-sm mt-1 block">{errors.message}</span>
                  )}
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-xl hover:opacity-90 transition-all duration-300 flex items-center justify-center space-x-2 group disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span>{loading ? 'Sending...' : 'Send Message'}</span>
                  {loading ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <svg className="w-5 h-5 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                    </svg>
                  )}
                </button>
              </form>

              {/* Contact Information */}
              <div className="mt-8 md:mt-12 pt-8 md:pt-12 border-t border-gray-200">
                <h2 className="text-xl md:text-2xl font-bold text-center mb-8 md:mb-12 gradient-text">Other Ways to Reach Us</h2>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
                  {/* Location */}
                  <div className="text-center p-6 rounded-2xl hover:bg-gray-50/50 transition-all group">
                    <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 rounded-xl bg-blue-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <i className="fas fa-location-dot text-blue-500 text-xl md:text-2xl"></i>
                    </div>
                    <h3 className="font-semibold mb-2 text-base md:text-lg">Location</h3>
                    <p className="text-gray-600 text-sm md:text-base">Mumbai, India</p>
                  </div>

                  {/* Email */}
                  <div className="text-center p-6 rounded-2xl hover:bg-gray-50/50 transition-all group">
                    <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 rounded-xl bg-green-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <i className="fas fa-envelope text-green-500 text-xl md:text-2xl"></i>
                    </div>
                    <h3 className="font-semibold mb-2 text-base md:text-lg">Email</h3>
                    <a href="mailto:invizher@gmail.com" className="text-gray-600 hover:text-blue-500 transition-colors text-sm md:text-base break-all">
                      invizher@gmail.com
                    </a>
                  </div>

                  {/* Social */}
                  <div className="text-center p-6 rounded-2xl hover:bg-gray-50/50 transition-all group">
                    <div className="w-14 h-14 md:w-16 md:h-16 mx-auto mb-4 rounded-xl bg-purple-500/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <i className="fas fa-share-nodes text-purple-500 text-xl md:text-2xl"></i>
                    </div>
                    <h3 className="font-semibold mb-2 text-base md:text-lg">Follow Us</h3>
                    <div className="flex justify-center space-x-4">
                      <a href="https://youtube.com/@invizher" className="text-gray-500 hover:text-red-600 transition-all hover:scale-110" title="YouTube">
                        <i className="fab fa-youtube text-xl"></i>
                      </a>
                      <a href="https://instagram.com/invizher" className="text-gray-500 hover:text-pink-600 transition-all hover:scale-110" title="Instagram">
                        <i className="fab fa-instagram text-xl"></i>
                      </a>
                      <a href="https://t.me/invizher" className="text-gray-500 hover:text-blue-500 transition-all hover:scale-110" title="Telegram">
                        <i className="fab fa-telegram text-xl"></i>
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            </div>
          </section>

          {/* FAQ Section */}
          <section className="container mx-auto px-4 sm:px-6 pb-16">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl md:text-3xl font-bold text-center mb-8 md:mb-12 gradient-text">Frequently Asked Questions</h2>
              <div className="grid gap-4 md:gap-6">
                <div className="glass-morphism rounded-xl md:rounded-2xl p-4 md:p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold mb-2 text-base md:text-lg flex items-start gap-3">
                    <i className="fas fa-clock text-blue-500 mt-1"></i>
                    <span>How quickly will I receive a response?</span>
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base ml-8">We typically respond within 24-48 hours during business days.</p>
                </div>
                <div className="glass-morphism rounded-xl md:rounded-2xl p-4 md:p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold mb-2 text-base md:text-lg flex items-start gap-3">
                    <i className="fas fa-handshake text-purple-500 mt-1"></i>
                    <span>Do you offer collaboration opportunities?</span>
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base ml-8">Yes! We're always open to collaboration. Please mention your proposal in the message.</p>
                </div>
                <div className="glass-morphism rounded-xl md:rounded-2xl p-4 md:p-6 hover:shadow-lg transition-shadow">
                  <h3 className="font-semibold mb-2 text-base md:text-lg flex items-start gap-3">
                    <i className="fas fa-comments text-green-500 mt-1"></i>
                    <span>Can I request tutorial topics?</span>
                  </h3>
                  <p className="text-gray-600 text-sm md:text-base ml-8">Absolutely! We love hearing what our community wants to learn about.</p>
                </div>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default Contact;
